import { types, clone, flow, getParent, getSnapshot } from 'mobx-state-tree';
import { User, NullUser } from './User';

const _pointsDescription = types.model('PointsDescription', {
    category: types.string,
    pointsAwarded: types.number,
    description: types.string,
    maxPoints: types.maybe(types.number),
    skipped: types.maybe(types.boolean),
    keyPointsCovered: types.maybe(types.array(types.string)),
    keyPointsMissed: types.maybe(types.array(types.string))
});

export const Assessment = types.model('Assessment', {
    id: types.string,
    aiGenerated: types.optional(types.boolean, false),
    companyId: types.string,
    salesperson: User,
    sessionType: types.optional(types.enumeration(['Elevator Pitch', 'Phone Call', 'Casual Encounter', 'Presentation', 'Online Video Conference', 'Sales Presentation', 'Sales Presentation / Demo', 'Investor Pitch', 'Objection Handling', 'Deal Coaching']), 'Phone Call'),
    subject: types.enumeration(['Company Overview', 'Company Overview (for MVP/Rollout)', 'Offerings', 'Objection Handling', 'Deal Coaching']),
    secondarySubject: types.maybe(types.string),
    reviewDateTime: types.maybe(types.string),
    videoName: types.maybe(types.string),
    completedDateTime: types.maybe(types.string),
    additionalAttendees: types.maybe(types.string),
    offeringPositives: types.maybe(types.string),
    offeringAreasForImprovement: types.maybe(types.string),
    companyBackgroundPositives: types.maybe(types.string),
    companyBackgroundAreasForImprovement: types.maybe(types.string),
    differentiatorsPositives: types.maybe(types.string),
    differentiatorsAreasForImprovement: types.maybe(types.string),
    prospectProfilePositives: types.maybe(types.string),
    prospectProfileAreasForImprovement: types.maybe(types.string),
    topCompetitorsPositives: types.maybe(types.string),
    topCompetitorsAreasForImprovement: types.maybe(types.string),
    companyOverviewPositives: types.maybe(types.string),
    companyOverviewAreasForImprovement: types.maybe(types.string),
    positives: types.maybe(types.array(types.string)),
    areasForImprovement: types.maybe(types.array(types.string)),
    grade: types.optional(types.enumeration(['red', 'yellow', 'green', 'notGraded']), 'notGraded'),
    reviewer: types.maybe(User),
    isDirty: types.optional(types.boolean, false),
    pointsDescriptions: types.maybe(types.array(_pointsDescription)),
    numberOfTries: types.optional(types.number, 1),
    ignoredQuestions: types.maybe(types.number),
    murphyInteractionStatus: types.maybe(types.enumeration(['ignored', 'responded'])),
    points: types.maybe(types.number),
    interactionId: types.maybe(types.string),
    qaAnalysis: types.maybe(types.frozen()),
    gradingStatus: types.maybe(types.string),
    recordingKey: types.maybe(types.string)
})
    .preProcessSnapshot(snapshot =>
    ({
        ...snapshot,
        numberOfTries: snapshot.numberOfTries != null ? Number(snapshot.numberOfTries) : undefined, // Convert value to a number, let types.optional default apply if missing
        // Migrate legacy subject/sessionType values
        sessionType: snapshot.sessionType === 'Q&A Practice' ? 'Objection Handling' : snapshot.sessionType,
        subject: snapshot.subject === 'Q&A Practice' ? 'Objection Handling'
            : snapshot.subject === 'Document Q&A' ? 'Deal Coaching'
            : snapshot.subject,
    }))
    .views(self => ({
        get areasForImprovementView() {
            if (!!self.pointsDescriptions) {
                return self.pointsDescriptions.filter(point => {
                    if (point.maxPoints) {
                        return point.pointsAwarded / point.maxPoints < 0.50;
                    }
                    return point.pointsAwarded === 0;
                }).map(point => point.description) ?? [];
            }
            return self.companyOverviewAreasForImprovement?.split('- ').slice(1) ?? [];
        },
        get positivesView() {
            if (!!self.pointsDescriptions) {
                return self.pointsDescriptions.filter(point => {
                    if (point.maxPoints) {
                        return point.pointsAwarded / point.maxPoints >= 0.50;
                    }
                    return point.pointsAwarded > 0;
                }).map(point => point.description) ?? [];
            }
            return self.companyOverviewPositives?.split('- ').slice(1) ?? [];
        },
        get colorForGrade() {

            if (self.grade === 'notGraded') {
                return 'gray';
            }

            return self.grade;
        },
        get assessmentCanBeSaved() {
            if (self.subject === 'Company Overview') {
                return !!self.companyOverviewPositives &&
                    !!self.companyOverviewAreasForImprovement &&
                    self.grade !== 'notGraded';
            } else {
                return !!self.offeringAreasForImprovement &&
                    !!self.offeringPositives &&
                    self.grade !== 'notGraded';
            }
        },
        get companyOverviewPositivesView() {
            if (!!self.companyOverviewPositives) {
                return self.companyOverviewPositives;
            }

            const positives = [];
            if (!!self.companyBackgroundPositives) {
                positives.push(`${self.companyBackgroundPositives}\n\n`);
            }

            if (!!self.differentiatorsPositives) {
                positives.push(`${self.differentiatorsPositives}\n\n`);
            }

            if (!!self.prospectProfilePositives) {
                positives.push(`${self.prospectProfilePositives}\n\n`);
            }

            if (!!self.topCompetitorsPositives) {
                positives.push(`${self.topCompetitorsPositives}\n\n`);
            }

            return positives.join('');
        },
        get companyOverviewAreasForImprovementView() {
            if (!!self.companyOverviewAreasForImprovement) {
                return self.companyOverviewAreasForImprovement;
            }

            const negatives = [];
            if (!!self.companyBackgroundAreasForImprovement) {
                negatives.push(`${self.companyBackgroundAreasForImprovement}\n\n`);
            }

            if (!!self.differentiatorsAreasForImprovement) {
                negatives.push(`${self.differentiatorsAreasForImprovement}\n\n`);
            }

            if (!!self.prospectProfileAreasForImprovement) {
                negatives.push(`${self.prospectProfileAreasForImprovement}\n\n`);
            }

            if (!!self.topCompetitorsAreasForImprovement) {
                negatives.push(`${self.topCompetitorsAreasForImprovement}\n\n`);
            }

            return negatives.join('');
        }
    }))
    .actions(self => {
        const updateFromEvent = (event) => {
            self[event.target.id] = event.target.value;
            self.isDirty = true;
        };
        const save = flow(function* save() {
            const snapshot = { ...getSnapshot(self) };
            delete snapshot.isDirty;
            self.isDirty = false;
            yield getParent(self, 2).updateAssessmentFromSnapshot(snapshot);
        });
        const setSessionType = (sessionType) => {
            self.sessionType = sessionType;
        };
        return {
            updateFromEvent,
            setSessionType,
            save
        };

    });

export const NullAssessment = Assessment.create({
    id: '',
    companyId: '',
    salesperson: clone(NullUser),
    sessionType: 'Phone Call',
    subject: 'Company Overview',
    reviewDateTime: '',
    completedDateTime: '',
    additionalAttendees: ''
});